import * as Sentry from '@sentry/react';
import { API } from '@unboared/react';
import { DocumentReference, Firestore, Timestamp, addDoc, collection, increment, updateDoc } from 'firebase/firestore';
import { BIG_SESSION_ALERT_PLAYERS } from '~/config/global.config';
import { normalizeScreenID } from '~/utils/screenID';
import { trackLivePlayers } from '~/utils/liveGamepads';
import { onStaleLink } from '~/services/screenLink/staleLinkWatchdog';
import { ClientDatabase } from '../client/firestore/client.firestore';
import { EndReason, SessionHealthTracker } from './SessionHealthTracker';

type NumPlayersTimestamp = {
    numPlayers: number;
    time: Timestamp;
};

export class FirestoreScreenDataCollector implements IDataCollector {
    private static SECONDS_BEFORE_COUNT: number = 10;

    /** Flush cadence — persists maxPlayers + a rolling endTimestamp so an abrupt
     * screen close (stop() never runs) keeps the session instead of losing it.
     * One write per screen every 3 min: negligible cost, ≤3 min recovery loss. */
    private static HEARTBEAT_MS: number = 180000;

    /** Sonde RTT : un message que l'écran s'envoie à lui-même. Il traverse le serveur
     * (`store.getSocketID` + emit) et revient : latence écran ↔ serveur de bout en bout,
     * sans rien changer côté serveur. Non relayé à l'iframe du jeu (seul TRANSMIT_CODE l'est). */
    private static RTT_PROBE_MS: number = 30000;

    private static ECHO_MESSAGE = 'ub::health-echo';

    /** Watchdog de la boucle JS : un tick manqué de > FREEZE_MS = écran gelé. */
    private static WATCHDOG_MS: number = 1000;

    private _db: Firestore;

    private _clientID: string;

    private _gameID: string;

    private _screenID: string;

    private _liveTeardown?: () => void;

    private _screenSessionDoc!: DocumentReference;

    private _startNumPlayerTime?: NumPlayersTimestamp;

    private _numMaxPlayers: number = 0;

    private _heartbeatTimer?: ReturnType<typeof setInterval>;

    private _stopped: boolean = false;

    /** Boîte noire (spec wiki `specs/boite-noire-session.md`), écrite dans `health`. */
    private _health = new SessionHealthTracker();

    private _healthUnsubs: Array<() => void> = [];

    private _rttTimer?: ReturnType<typeof setInterval>;

    private _watchdogTimer?: ReturnType<typeof setInterval>;

    /** Alerte « grosse soirée » déjà émise pour cette session (une seule par screenSession). */
    private _bigSessionAlerted: boolean = false;

    constructor(db: Firestore, clientID: string, gameID: string, screenID: string) {
        this._db = db;
        this._clientID = clientID;
        this._gameID = gameID;
        this._screenID = normalizeScreenID(screenID);
    }

    start() {
        // Create screen session document
        addDoc(this._collectionRef(), this._makeNewDocument()).then((docRef) => {
            // stop() may have been called before this addDoc resolved (fast game
            // switch / unmount). Don't wire up listeners/timers that would then leak —
            // but still close out the just-created doc so it is not orphaned without an end.
            if (this._stopped) {
                updateDoc(docRef, { endTimestamp: Timestamp.now(), maxPlayers: this._numMaxPlayers }).catch((e) =>
                    console.error(e)
                );
                return;
            }
            this._screenSessionDoc = docRef;
            // Corrèle chaque erreur Sentry à sa ligne screenSession Firestore (boîte noire, spec
            // boite-noire-session.md) : client / jeu / écran / session lisibles dans l'issue.
            Sentry.setTags({
                clientID: this._clientID,
                gameID: this._gameID,
                screenID: this._screenID,
                screenSessionID: docRef.id,
            });
            // Shared seed/subscribe/reconcile lifecycle (cf gotcha #111/#103, same primitive
            // as useScreenPlayerCount and FirestoreGamepadDataCollector):
            // - onDisconnect: close the running interval and reopen it at the new count.
            // - onConnect / onDeviceBack: re-evaluate the count (onDeviceBack = grace-period
            //   reconnection, fires instead of onConnect — gotcha #2).
            // - onSeed: seed maxPlayers + the first interval with players already present at
            //   start (e.g. after a free-play game switch); onConnect/onDeviceBack do not
            //   replay already-connected devices.
            // - onReconcile: re-read getGamepadIDs() so maxPlayers converges to the true count
            //   when the device list synced late after a screen reload (seed read 0).
            this._liveTeardown = trackLivePlayers({
                onDisconnect: () => {
                    this._stopNumPlayerDuration();
                    this._startNumPlayerDuration(API.getGamepadIDs().length);
                },
                onConnect: () => {
                    this._health.noteConnect();
                    this._onPlayerCountChange();
                },
                onDeviceBack: () => {
                    this._health.noteGamepadBack();
                    this._onPlayerCountChange();
                },
                onSeed: () => {
                    const initialPlayers = API.getGamepadIDs().length;
                    if (initialPlayers > this._numMaxPlayers) {
                        this._numMaxPlayers = initialPlayers;
                    }
                    this._maybeRaiseBigSessionAlert(initialPlayers);
                    this._startNumPlayerDuration(initialPlayers);
                },
                onReconcile: () => this._reconcile(),
            });
            ClientDatabase.startScreenSession(this._clientID, this._screenID, this._screenSessionDoc.id, this._gameID);

            // Heartbeat: an animator who just closes the tab never triggers stop(), so
            // without this the whole evening's maxPlayers + endTimestamp are never written.
            this._heartbeatTimer = setInterval(() => this._flush(), FirestoreScreenDataCollector.HEARTBEAT_MS);
            this._startHealthProbes();
        });
    }

    stop(reason?: EndReason) {
        this._stopped = true;
        // La première raison gagne : `session_closed` (serveur) posé avant le démontage
        // React ne doit pas être écrasé par `unmount`.
        if (reason && !this._health.endReason) {
            this._health.setEndReason(reason);
        }
        this._stopHealthProbes();
        // Tear down reconcile interval + listeners synchronously (was undefined if stop()
        // ran before the start() addDoc resolved — the _stopped guard there skips wiring).
        if (this._liveTeardown) {
            this._liveTeardown();
            this._liveTeardown = undefined;
        }
        if (this._heartbeatTimer) {
            clearInterval(this._heartbeatTimer);
            this._heartbeatTimer = undefined;
        }
        this._stopNumPlayerDuration();
        if (!this._screenSessionDoc) {
            return;
        }
        // Stop the screen session
        updateDoc(this._screenSessionDoc, {
            endTimestamp: Timestamp.now(),
            maxPlayers: this._numMaxPlayers,
            health: this._healthPayload(),
        }).catch((e) => {
            console.error(e);
        });
        // ClientDatabase.stopScreenSession(this._clientID, this._screenID);
    }

    /** Branche la boîte noire sur la lib + les sondes locales. Tout est en mémoire et
     * ne part en base qu'au heartbeat (aucune écriture supplémentaire). Chaque signal
     * laisse aussi un breadcrumb Sentry : une issue montre alors ce qui précédait. */
    private _startHealthProbes() {
        const crumb = (message: string, data?: Record<string, unknown>) =>
            Sentry.addBreadcrumb({ category: 'session', level: 'info', message, data });

        this._healthUnsubs.push(
            API.onDeviceAway((deviceID: string) => {
                this._health.noteGamepadAway();
                crumb('gamepad away', { deviceID });
            }),
            API.onTemporaryDisconnect(() => {
                this._health.noteHostAway();
                crumb('screen socket lost');
            }),
            // onReady est aussi dispatché à la toute première connexion : noteHostBack
            // ne compte que s'il y avait un blip ouvert.
            API.onReady(() => this._health.noteHostBack()),
            API.onSessionClosed(() => {
                // Le serveur a tué la session (grace host expirée, deleteSession…). On
                // fige la raison et on pousse tout de suite : le démontage React qui suit
                // ne doit rien perdre.
                if (!this._health.endReason) {
                    this._health.setEndReason('session_closed');
                }
                crumb('session closed by server');
                this._flush();
            }),
            // Breadcrumb Sentry déjà posé par ScreenStaleLinkReconnect.
            onStaleLink(({ acted }) => this._health.noteStaleLink(acted)),
            API.onMessage(FirestoreScreenDataCollector.ECHO_MESSAGE, (from: string, data?: { t?: number }) => {
                if (from === API.getDeviceID() && typeof data?.t === 'number') {
                    this._health.noteRtt(Date.now() - data.t);
                }
            })
        );

        const probe = () => {
            try {
                API.send(API.getDeviceID(), FirestoreScreenDataCollector.ECHO_MESSAGE, { t: Date.now() });
            } catch {
                // Device pas encore enregistré (gotcha #138) ou socket away : pas d'échantillon.
            }
        };
        probe();
        this._rttTimer = setInterval(probe, FirestoreScreenDataCollector.RTT_PROBE_MS);

        this._watchdogTimer = setInterval(
            () => this._health.tick(typeof document !== 'undefined' && document.hidden),
            FirestoreScreenDataCollector.WATCHDOG_MS
        );
    }

    private _stopHealthProbes() {
        this._healthUnsubs.forEach((unsub) => unsub());
        this._healthUnsubs = [];
        if (this._rttTimer) {
            clearInterval(this._rttTimer);
            this._rttTimer = undefined;
        }
        if (this._watchdogTimer) {
            clearInterval(this._watchdogTimer);
            this._watchdogTimer = undefined;
        }
    }

    /** Snapshot prêt à écrire. `lastHeartbeat` permet d'inférer une fin brutale à la
     * lecture : pas d'`endReason` ET lastHeartbeat > 2 × HEARTBEAT_MS avant endTimestamp. */
    private _healthPayload() {
        return { ...this._health.snapshot(this._numMaxPlayers), lastHeartbeat: Timestamp.now() };
    }

    /** Re-read the true player count; only react on a change so the usage-interval
     * clock is not reset on every tick (which would drop sub-threshold intervals). */
    private _reconcile() {
        const nPlayers = API.getGamepadIDs().length;
        if (nPlayers !== (this._startNumPlayerTime?.numPlayers ?? 0)) {
            this._onPlayerCountChange();
        }
    }

    /** Persist current peak + a rolling endTimestamp (best effort, only when a doc exists).
     * Also checkpoint the in-progress usage interval: without this, a session that keeps a
     * stable player count for the whole evening and is then closed abruptly (tab close →
     * stop() never runs) accumulates its usage only inside the still-open interval, which is
     * never flushed → usageInSecondes stays `{}`. That is the main reason the field is so
     * often empty. Checkpointing every heartbeat makes usageInSecondes accrue continuously
     * and survive an abrupt close (last ≤3 min excepted).
     *
     * The checkpoint advances the open interval's start time in place (no close/reopen, no
     * `{n}P_interval` sub-doc — that collection is write-only, one addDoc per heartbeat
     * would be pure noise) and is merged into the single heartbeat updateDoc: one billed
     * write instead of two, and no partial-failure window between increment and timestamps.
     * An interval younger than SECONDS_BEFORE_COUNT is left untouched (it keeps accruing —
     * resetting its clock would silently drop those seconds), matching the guard in
     * _stopNumPlayerDuration. */
    private _flush() {
        if (!this._screenSessionDoc) {
            return;
        }
        // Un point de timeline par heartbeat (nombre de joueurs à cet instant).
        this._health.notePlayers(API.getGamepadIDs().length);
        const payload: Record<string, unknown> = {
            endTimestamp: Timestamp.now(),
            maxPlayers: this._numMaxPlayers,
            health: this._healthPayload(),
        };
        if (this._startNumPlayerTime) {
            const npStart = this._startNumPlayerTime.time;
            const { numPlayers } = this._startNumPlayerTime;
            const npStop = Timestamp.now();
            const interval = npStop.seconds - npStart.seconds;
            if (interval > FirestoreScreenDataCollector.SECONDS_BEFORE_COUNT) {
                payload[`usageInSecondes.${numPlayers}P`] = increment(interval);
                // Advance to npStop (not a fresh now()) so consecutive checkpoints tile
                // the timeline with no gap.
                this._startNumPlayerTime = { numPlayers, time: npStop };
            }
        }
        updateDoc(this._screenSessionDoc, payload).catch((e) => console.error(e));
    }

    private _onPlayerCountChange() {
        this._stopNumPlayerDuration();
        const nPlayers = API.getGamepadIDs().length;
        if (nPlayers > this._numMaxPlayers) {
            this._numMaxPlayers = nPlayers;
        }
        this._maybeRaiseBigSessionAlert(nPlayers);
        this._startNumPlayerDuration(nPlayers);
    }

    /** Franchissement du seuil BIG_SESSION_ALERT_PLAYERS : un doc `alerts` (type
     * big_session), une seule fois par session. La Cloud Function bigSessionAlert
     * (unboared-b2b/functions) envoie l'email interne. Rien côté client. Le nombre
     * lu inclut les manettes en grace period (getGamepadIDs, gotcha #123) : c'est
     * voulu, on veut être prévenu tôt, pas exact. */
    private _maybeRaiseBigSessionAlert(nPlayers: number) {
        if (this._bigSessionAlerted || nPlayers < BIG_SESSION_ALERT_PLAYERS || !this._screenSessionDoc) {
            return;
        }
        this._bigSessionAlerted = true;
        Sentry.addBreadcrumb({
            category: 'session',
            level: 'warning',
            message: 'big session threshold crossed',
            data: { players: nPlayers },
        });
        addDoc(collection(this._db, 'alerts'), {
            type: 'big_session',
            clientID: this._clientID,
            gameID: this._gameID,
            screenID: this._screenID,
            screenSessionID: this._screenSessionDoc.id,
            players: nPlayers,
            threshold: BIG_SESSION_ALERT_PLAYERS,
            at: Timestamp.now(),
        }).catch((e) => console.error('big session alert failed', e));
    }

    private _stopNumPlayerDuration() {
        if (this._startNumPlayerTime) {
            const npStart = this._startNumPlayerTime.time;
            const { numPlayers } = this._startNumPlayerTime;
            const npStop = Timestamp.now();
            const interval = npStop.seconds - npStart.seconds;
            if (interval > FirestoreScreenDataCollector.SECONDS_BEFORE_COUNT) {
                updateDoc(this._screenSessionDoc, {
                    [`usageInSecondes.${numPlayers}P`]: increment(interval),
                }).catch((e) => console.error(e));
                // Sans catch, une écriture rejouée par le SDK après une micro-coupure
                // (déjà enregistrée côté serveur) remontait en « Document already
                // exists » non géré dans Sentry — sans aucune perte de donnée.
                this._saveNPlayersInterval(numPlayers, npStart, npStop).catch((e) => console.error(e));
                this._startNumPlayerTime = undefined;
            }
        }
    }

    private _startNumPlayerDuration(numPlayers: number) {
        this._startNumPlayerTime = {
            numPlayers,
            time: Timestamp.now(),
        };
    }

    /** ⚠️ Since heartbeat checkpointing (2026-07), these sub-docs are PARTIAL segments:
     * an interval written on a count change only covers the span since the last heartbeat
     * checkpoint (≤3 min), and checkpointed spans produce no sub-doc at all. Do not sum
     * them to reconstruct durations — use the parent doc's usageInSecondes instead.
     * (Nothing reads this collection today; candidate for removal.) */
    private async _saveNPlayersInterval(numPlayers: number, startTimestamp: Timestamp, endTimestamp: Timestamp) {
        return addDoc(
            collection(
                this._db,
                'clients',
                this._clientID,
                'screenSession',
                this._screenSessionDoc.id,
                `${numPlayers}P_interval`
            ),
            { startTimestamp, endTimestamp }
        );
    }

    private _collectionRef() {
        return collection(this._db, 'clients', this._clientID, 'screenSession');
    }

    private _makeNewDocument() {
        return {
            startTimestamp: Timestamp.now(),
            gameID: this._gameID,
            screenID: this._screenID,
            usageInSecondes: {},
            maxPlayers: 0,
        };
    }
}
